import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

import Login from './pages/Login';
import Notifications from './pages/Notifications';
import ChangePassword from './pages/ChangePassword';
import Chat from './pages/Chat';
import Expenses from './pages/Expenses';
import AdminMessages from './pages/AdminMessages';
import ActivityLog from './pages/admin/ActivityLog';
import Vault from './pages/Vault';
import AdminVault from './pages/admin/Vault';
import CompanyDocuments from './pages/CompanyDocuments';

// Employee pages
import EmployeeDashboard from './pages/employee/Dashboard';
import MyTasks from './pages/employee/MyTasks';
import MyTimesheet from './pages/employee/MyTimesheet';
import AddTask from './pages/employee/AddTask';
import TaskDetail from './pages/employee/TaskDetail';
import EmployeeIssues from './pages/employee/Issues';
import EmployeeConcerns from './pages/employee/Concerns';

// HR pages
import HRDashboard from './pages/hr/Dashboard';
import AllTasks from './pages/hr/AllTasks';
import AssignTask from './pages/hr/AssignTask';
import Employees from './pages/hr/Employees';
import EmployeeDetail from './pages/hr/EmployeeDetail';
import HRIssues from './pages/hr/Issues';
import HRConcerns from './pages/hr/Concerns';
import HRLeaves from './pages/hr/Leaves';
import WeeklyMeeting from './pages/hr/WeeklyMeeting';

// Student Onboarding
import Onboarding from './pages/onboarding/Onboarding';
import NewStudent from './pages/onboarding/NewStudent';
import OnboardingDetail from './pages/onboarding/OnboardingDetail';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import CreateUser from './pages/admin/CreateUser';
import EditUser from './pages/admin/EditUser';

// Employee extra pages
import Leaves from './pages/employee/Leaves';
import Feedback from './pages/employee/Feedback';

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/admin/dashboard" replace />;
  if (user.role === 'hr') return <Navigate to="/hr/dashboard" replace />;
  return <Navigate to="/employee/dashboard" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<RootRedirect />} />

          {/* Employee routes */}
          <Route element={<ProtectedRoute roles={['employee', 'hr', 'super_admin']} />}>
            <Route element={<Layout />}>
              <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
              <Route path="/employee/tasks" element={<MyTasks />} />
              <Route path="/employee/tasks/new" element={<AddTask />} />
              <Route path="/employee/tasks/:id" element={<TaskDetail />} />
              <Route path="/employee/issues" element={<EmployeeIssues />} />
              <Route path="/employee/concerns" element={<EmployeeConcerns />} />
              <Route path="/employee/leaves" element={<Leaves />} />
              <Route path="/employee/timesheet" element={<MyTimesheet />} />
              <Route path="/employee/feedback" element={<Feedback />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/chat/:roomId" element={<Chat />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/vault" element={<Vault />} />
              <Route path="/documents" element={<CompanyDocuments />} />
              <Route path="/admin-messages" element={<AdminMessages />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/onboarding/new" element={<NewStudent />} />
              <Route path="/onboarding/:id" element={<OnboardingDetail />} />
              <Route path="/notifications" element={<Notifications />} />
            </Route>
          </Route>

          {/* HR routes */}
          <Route element={<ProtectedRoute roles={['hr', 'super_admin']} />}>
            <Route element={<Layout />}>
              <Route path="/hr/dashboard" element={<HRDashboard />} />
              <Route path="/hr/tasks" element={<AllTasks />} />
              <Route path="/hr/tasks/assign" element={<AssignTask />} />
              <Route path="/hr/tasks/:id" element={<TaskDetail />} />
              <Route path="/hr/employees" element={<Employees />} />
              <Route path="/hr/employees/:id" element={<EmployeeDetail />} />
              <Route path="/hr/issues" element={<HRIssues />} />
              <Route path="/hr/concerns" element={<HRConcerns />} />
              <Route path="/hr/leaves" element={<HRLeaves />} />
              <Route path="/hr/meetings" element={<WeeklyMeeting />} />
            </Route>
          </Route>

          {/* Admin routes */}
          <Route element={<ProtectedRoute roles={['super_admin']} />}>
            <Route element={<Layout />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/users/new" element={<CreateUser />} />
              <Route path="/admin/users/:id/edit" element={<EditUser />} />
              <Route path="/admin/activity-log" element={<ActivityLog />} />
              <Route path="/admin/vault" element={<AdminVault />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
